const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1'

/** Mirrors the API's error envelope. The UI prints `messageFa` and branches on `code`. */
export class ApiError extends Error {
  readonly statusCode: number
  readonly code: string
  readonly messageFa: string
  readonly fields?: Record<string, string>

  constructor(payload: {
    statusCode: number
    code: string
    messageFa: string
    fields?: Record<string, string>
  }) {
    super(`${payload.code} (${payload.statusCode})`)
    this.name = 'ApiError'
    this.statusCode = payload.statusCode
    this.code = payload.code
    this.messageFa = payload.messageFa
    this.fields = payload.fields
  }
}

const NETWORK_ERROR_FA = 'ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید.'

/** Held in memory only — the httpOnly refresh cookie is what survives a reload. */
let accessToken: string | null = null

export const setAccessToken = (token: string | null): void => {
  accessToken = token
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH'
  body?: unknown
  /** False on the auth endpoints themselves, so a failed refresh cannot recurse. */
  retryOnUnauthorized?: boolean
  /** True only for the refresh call, which must not wait on its own bootstrap. */
  skipSessionWait?: boolean
}

type SessionListener = (staff: unknown | null) => void
const sessionListeners = new Set<SessionListener>()

export function onSessionChange(listener: SessionListener): () => void {
  sessionListeners.add(listener)
  return () => sessionListeners.delete(listener)
}

let refreshInFlight: Promise<boolean> | null = null

/**
 * Trades the refresh cookie for a new access token. Single-flight, as in the customer app: the
 * API ends the session when a refresh token is presented twice, so two parallel refreshes would
 * look like a stolen cookie and sign the staff member out.
 */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const data = await apiFetch<{ accessToken: string; staff: unknown }>('/admin/auth/refresh', {
        method: 'POST',
        retryOnUnauthorized: false,
        skipSessionWait: true,
      })
      accessToken = data.accessToken
      for (const listener of sessionListeners) listener(data.staff)
      return true
    } catch {
      accessToken = null
      for (const listener of sessionListeners) listener(null)
      return false
    } finally {
      queueMicrotask(() => {
        refreshInFlight = null
      })
    }
  })()

  return refreshInFlight
}

/** Started at module load so no request can race the session restore. */
export const sessionBootstrap: Promise<boolean> = refreshSession()

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, retryOnUnauthorized = true, skipSessionWait } = options

  if (!skipSessionWait) await sessionBootstrap.catch(() => false)

  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`

  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? null : JSON.stringify(body),
    })
  } catch {
    throw new ApiError({ statusCode: 0, code: 'NETWORK', messageFa: NETWORK_ERROR_FA })
  }

  if (response.status === 204) return undefined as T

  const payload: unknown = await response.json().catch(() => null)

  if (!response.ok) {
    if (response.status === 401 && retryOnUnauthorized) {
      const refreshed = await refreshSession()
      if (refreshed) return apiFetch<T>(path, { ...options, retryOnUnauthorized: false })
    }

    if (payload && typeof payload === 'object' && 'code' in payload) {
      throw new ApiError(payload as ConstructorParameters<typeof ApiError>[0])
    }
    throw new ApiError({
      statusCode: response.status,
      code: 'INTERNAL',
      messageFa: 'مشکلی در سامانه پیش آمد. لطفاً دوباره تلاش کنید.',
    })
  }

  return payload as T
}

/**
 * For the HTML policy document. Same session handling as `apiFetch`, but the body is the page
 * itself, not JSON.
 */
export async function apiFetchText(path: string, retryOnUnauthorized = true): Promise<string> {
  await sessionBootstrap.catch(() => false)

  const headers: Record<string, string> = {}
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`

  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, { headers, credentials: 'include' })
  } catch {
    throw new ApiError({ statusCode: 0, code: 'NETWORK', messageFa: NETWORK_ERROR_FA })
  }

  if (response.status === 401 && retryOnUnauthorized && (await refreshSession())) {
    return apiFetchText(path, false)
  }

  if (!response.ok) {
    throw new ApiError({
      statusCode: response.status,
      code: response.status === 404 ? 'NOT_FOUND' : 'INTERNAL',
      messageFa: 'دریافت بیمه‌نامه ممکن نشد.',
    })
  }

  return response.text()
}
